import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { canWrite, WRITE_PERMISSION_DENIED } from '@/services/permissions';
import { recordAudit } from '@/services/audit';

// =============================================================
// Pagos administrativos (módulo "Pagos" del panel)
// =============================================================

export interface Payment {
  id: string;
  paymentNumber: string;
  orderNumber: string;
  saleNumber: string;
  method: string;
  status: string;
  amount: number;
  transactionRef: string;
  createdAt: string;
}

export interface PaymentsResult {
  data: Payment[];
}

export const METHOD_LABELS: Record<string, string> = {
  nequi_negocios: 'Nequi Negocios',
  payment_link: 'ePayco',
  pse: 'PSE',
  card_debit: 'Tarjeta débito',
  card_credit: 'Tarjeta crédito',
  transfer: 'Transferencia',
  cash: 'Efectivo',
  other: 'Otro',
  sistecredito: 'Sistecrédito',
  // Compatibilidad con valores históricos de ventas directas
  nequi: 'Nequi',
  card: 'Tarjeta',
};

export const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendiente',
  initiated: 'Iniciado',
  approved: 'Aprobado',
  rejected: 'Rechazado',
  cancelled: 'Cancelado',
  expired: 'Expirado',
  refunded: 'Reembolsado',
  partially_refunded: 'Reembolso parcial',
};

// Métodos de pago en línea directa (se aprueban automáticamente). Contra
// entrega (cash), transferencia y Nequi quedan pendientes hasta confirmación
// manual; tarjeta, PSE y pago en línea se aprueban al momento.
const ONLINE_METHODS = new Set([
  'payment_link',
  'pse',
  'card_debit',
  'card_credit',
]);

export function defaultStatusForMethod(method: string): string {
  return ONLINE_METHODS.has(method) ? 'approved' : 'pending';
}

export async function fetchPayments(): Promise<PaymentsResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    const paymentsRes = await supabase.from('payments').select('*');
    if (paymentsRes.error) throw new Error(paymentsRes.error.message);

    // Lookups de enriquecimiento (nº de pedido / venta). No son críticos:
    // si alguno falla por red, seguimos mostrando los pagos con '—'.
    const orderMap = new Map<string, string>();
    const saleMap = new Map<string, string>();
    try {
      const [ordersRes, salesRes] = await Promise.all([
        supabase.from('orders').select('id, order_number'),
        supabase.from('sales').select('id, sale_number'),
      ]);
      if (!ordersRes.error) {
        ((ordersRes.data ?? []) as Array<Record<string, any>>).forEach((o) =>
          orderMap.set(o.id, o.order_number),
        );
      }
      if (!salesRes.error) {
        ((salesRes.data ?? []) as Array<Record<string, any>>).forEach((s) =>
          saleMap.set(s.id, s.sale_number),
        );
      }
    } catch (lookupErr) {
      console.warn('[payments] lookups no críticos fallaron:', lookupErr);
    }

    const mapped: Payment[] = ((paymentsRes.data ?? []) as Array<Record<string, any>>)
      .map((p) => ({
        id: p.id,
        paymentNumber: p.payment_number ?? '',
        orderNumber: p.order_id ? orderMap.get(p.order_id) ?? '' : '',
        saleNumber: p.sale_id ? saleMap.get(p.sale_id) ?? '' : '',
        method: p.method,
        status: p.status,
        amount: Number(p.amount),
        transactionRef: p.transaction_ref ?? '',
        createdAt: p.created_at ?? '',
      }))
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));

    return { data: mapped };
  } catch (err) {
    console.error('[payments] connection failed:', err);
    throw err;
  }
}

export interface PaymentInput {
  amount: number;
  method: string;
  status: string;
  transactionRef: string;
  saleId: string;
}

// Crea un pago manual vinculado a una venta.
export async function savePayment(
  input: PaymentInput,
): Promise<{ data: Payment | null; error: string | null }> {
  if (!(await canWrite('payments'))) {
    return { data: null, error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { data: null, error: 'Supabase no está configurado.' };
  }

  const paymentNumber = `P-${Date.now().toString().slice(-6)}`;

  const payload: Record<string, any> = {
    payment_number: paymentNumber,
    method: input.method,
    status: input.status,
    amount: Number(input.amount),
    transaction_ref: input.transactionRef.trim() || null,
  };
  if (input.saleId) payload.sale_id = input.saleId;

  try {
    const { data, error } = await supabase.from('payments').insert(payload).select('*');
    if (error) return { data: null, error: error.message };
    const row = data?.[0] as Record<string, any> | undefined;
    if (!row) return { data: null, error: 'No se pudo leer el pago guardado.' };
    recordAudit({
      entityType: 'payments',
      entityId: row.id,
      action: 'CREATE',
      newValue: `$${input.amount}`,
    });
    return {
      data: {
        id: row.id,
        paymentNumber: row.payment_number ?? '',
        orderNumber: '',
        saleNumber: '',
        method: row.method,
        status: row.status,
        amount: Number(row.amount),
        transactionRef: row.transaction_ref ?? '',
        createdAt: row.created_at ?? '',
      },
      error: null,
    };
  } catch (err) {
    console.warn('[payments] save failed:', err);
    return { data: null, error: 'Error de conexión al guardar el pago.' };
  }
}

// Cambia el estado de un pago manualmente (pending/approved/rejected/etc.)
// dejando registro en auditoría de quién lo hizo.
export async function updatePaymentStatus(
  id: string,
  status: string,
): Promise<{ error: string | null }> {
  if (!(await canWrite('payments'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  const { data: current } = await supabase
    .from('payments')
    .select('status')
    .eq('id', id)
    .maybeSingle();

  const { error } = await supabase.from('payments').update({ status }).eq('id', id);
  if (error) return { error: error.message };

  recordAudit({
    entityType: 'payments',
    entityId: id,
    action: 'UPDATE',
    fieldName: 'status',
    oldValue: (current as Record<string, any> | null)?.status ?? '',
    newValue: status,
  });
  return { error: null };
}

// Consulta los pagos asociados a un pedido (para mostrar abonos parciales en
// el módulo de Producción).
export async function fetchOrderPayments(orderId: string): Promise<Payment[]> {
  if (!isSupabaseConfigured || !supabase) return [];
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('order_id', orderId)
    .order('created_at', { ascending: false });
  if (error) {
    console.warn('[payments] fetchOrderPayments error', error.message);
    return [];
  }
  return ((data ?? []) as Array<Record<string, any>>).map((p) => ({
    id: p.id,
    paymentNumber: p.payment_number ?? '',
    orderNumber: '',
    saleNumber: '',
    method: p.method,
    status: p.status,
    amount: Number(p.amount),
    transactionRef: p.transaction_ref ?? '',
    createdAt: p.created_at ?? '',
  }));
}

export interface OrderPaymentInput {
  orderId: string;
  amount: number;
  method: string;
  status: string;
  transactionRef: string;
}

// Registra un abono (pago parcial) vinculado a un pedido de producción.
export async function recordOrderPayment(
  input: OrderPaymentInput,
): Promise<{ error: string | null }> {
  if (!(await canWrite('payments'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  const paymentNumber = `P-${Date.now().toString().slice(-6)}`;

  const { error } = await supabase.from('payments').insert({
    payment_number: paymentNumber,
    order_id: input.orderId,
    method: input.method,
    status: input.status,
    amount: Number(input.amount),
    transaction_ref: input.transactionRef.trim() || null,
  });
  if (error) return { error: error.message };

  recordAudit({
    entityType: 'payments',
    entityId: input.orderId,
    action: 'CREATE',
    newValue: `$${input.amount}`,
  });
  return { error: null };
}